'use client'

import { AxiosError } from 'axios'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useEffect, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { resetPassword } from '../services/api'

const formSchema = z.object({
  new_password: z
    .string()
    .min(8, 'Şifre en az 8 karakter olmalıdır')
    .regex(/^(?=.*[a-zA-Z])/, 'Şifre sadece rakamlardan oluşamaz')
    .refine(
      (password) => {
        // CommonPasswordValidator - çok yaygın şifreleri engelle
        const commonPasswords = ['password', '12345678', 'qwerty', 'abc123', 'password123', '123456789']
        return !commonPasswords.includes(password.toLowerCase())
      },
      { message: 'Bu şifre çok yaygın kullanılıyor' }
    ),
  new_password_confirm: z.string().min(1, 'Şifre tekrarı zorunludur'),
}).refine((data) => data.new_password === data.new_password_confirm, {
  message: 'Şifreler eşleşmiyor',
  path: ['new_password_confirm'],
})

type FormValues = z.infer<typeof formSchema>

export function useResetPasswordForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const token = searchParams.get('token')
  const tenant = searchParams.get('tenant')

  const [isSuccess, setIsSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      new_password: '',
      new_password_confirm: '',
    },
  })

  const onSubmit = async (values: FormValues) => {
    if (!token) {
      setErrorMessage('Geçersiz sıfırlama bağlantısı')
      return
    }

    setErrorMessage(null)
    setIsSuccess(false)
    setIsLoading(true)

    try {
      await resetPassword({
        token,
        new_password: values.new_password,
        new_password_confirm: values.new_password_confirm,
        tenant: tenant || undefined,
      })
      setIsSuccess(true)

    } catch (error) {
      if (error instanceof AxiosError) {
        setErrorMessage(error.response?.data?.error || 'Beklenmeyen bir hata oluştu. Lütfen daha sonra tekrar deneyin.')
      } else {
        setErrorMessage('Beklenmeyen bir hata oluştu. Lütfen daha sonra tekrar deneyin.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isSuccess) return
    const timer = window.setTimeout(() => router.push('/login'), 2000)
    return () => window.clearTimeout(timer)
  }, [isSuccess, router])

  return { form, isSuccess, isLoading, errorMessage, onSubmit, token }
}
